import { useEffect, useMemo } from 'react';
import { useCurrenciesContext, useSecuritiesContext } from '../../../contexts';
import { useWLTradableCustomerMarketAccounts } from '../../../hooks';
import { useWLCustomerMarketAccountContext } from '../../../providers/WLCustomerMarketAccountsProvider';
import type { OrderStrategy, Security } from '../../../types';
import { BufferedDispatcher, EMPTY_ARRAY } from '../../../utils';
import { useWLOrderFormDispatch, useWLOrderFormSelector, type WLRootState } from '../WLOrderFormStore';
import {
  setCurrencies,
  setMarketAccounts,
  setSecurities,
  setStrategiesBySymbol,
} from './OMSReferenceDataSlice/reducer';
import { setReferenceData as setOrderSliceReferenceData } from './OrderSlice/reducer';
import { setSwapReferenceData } from './SwapSlice/reducer';

export const useOMSDependencies = ({
  getStrategiesGivenSecurity,
}: {
  getStrategiesGivenSecurity: (s: Security) => OrderStrategy[];
}): void => {
  const dispatch = useWLOrderFormDispatch();
  const dispatcher = useMemo(() => new BufferedDispatcher(dispatch), [dispatch]);

  const { securitiesBySymbol, securitiesList } = useSecuritiesContext();
  const currencies = useCurrenciesContext();

  useEffect(() => {
    dispatcher.dispatchImmediate(setCurrencies(currencies));
  }, [dispatcher, currencies]);

  useEffect(() => {
    dispatcher.dispatchImmediate(
      setSecurities({
        securitiesList,
        securitiesBySymbol,
      })
    );
  }, [dispatcher, securitiesList, securitiesBySymbol]);

  const {
    customerMarketAccountsList: allCustomerMarketAccounts = EMPTY_ARRAY,
    customerMarketAccountsBySourceAccountID,
  } = useWLCustomerMarketAccountContext();

  const tradableMarketAccountsSet = useWLTradableCustomerMarketAccounts();

  const tradableMarketAccounts = useMemo(() => {
    if (!tradableMarketAccountsSet) {
      return allCustomerMarketAccounts;
    }
    return allCustomerMarketAccounts.filter(account => tradableMarketAccountsSet.has(account.SourceAccountID));
  }, [allCustomerMarketAccounts, tradableMarketAccountsSet]);

  const strategiesBySymbol = useMemo(() => {
    const strategiesBySymbol = new Map<string, OrderStrategy[]>();
    for (const security of securitiesList) {
      strategiesBySymbol.set(security.Symbol, getStrategiesGivenSecurity(security));
    }
    return strategiesBySymbol;
  }, [getStrategiesGivenSecurity, securitiesList]);

  useEffect(() => {
    dispatcher.dispatchImmediate(setStrategiesBySymbol(strategiesBySymbol));
  }, [strategiesBySymbol, dispatcher]);

  useEffect(() => {
    if (tradableMarketAccounts && customerMarketAccountsBySourceAccountID) {
      dispatcher.dispatchImmediate(
        setMarketAccounts({
          customerMarketAccountsList: tradableMarketAccounts,
          customerMarketAccountsBySourceAccountID: new Map(customerMarketAccountsBySourceAccountID),
        })
      );
    }
  }, [dispatcher, tradableMarketAccounts, customerMarketAccountsBySourceAccountID]);

  // Sharing references
  const omsReferenceDataState = useWLOrderFormSelector((state: WLRootState) => state.referenceData);
  useEffect(() => {
    dispatch(setOrderSliceReferenceData(omsReferenceDataState));
    dispatch(setSwapReferenceData(omsReferenceDataState));
  }, [dispatch, omsReferenceDataState]);
};
